import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api, { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Lock, Mail, CheckCircle } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail });
      setForgotSent(true);
    } catch {} finally { setForgotLoading(false); }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-[#0D8B7D] to-[#0A6F64] items-center justify-center p-12">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'url(https://images.pexels.com/photos/35545959/pexels-photo-35545959.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)', backgroundSize: 'cover', backgroundPosition: 'center'}} />
        <div className="relative z-10 text-center max-w-md">
          <img src="https://mindcareindia.com/wp-content/uploads/2022/01/logo-TMC-NEW.png" alt="Mind Care" className="h-20 mx-auto mb-8" />
          <h1 className="text-4xl font-semibold text-white font-['Outfit'] tracking-tight mb-4">The Mind Care</h1>
          <p className="text-lg text-white/80 font-['Work_Sans']">We Treat Without Medicine</p>
          <div className="mt-10 p-6 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20">
            <p className="text-white/90 text-sm italic leading-relaxed">"Mental health is not a destination, but a process. It's about how you drive, not where you're going."</p>
            <p className="text-white/60 text-xs mt-2">- Noam Shpancer</p>
          </div>
        </div>
      </div>

      {/* Right - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-[#F4FAF9]">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <img src="https://mindcareindia.com/wp-content/uploads/2022/01/logo-TMC-NEW.png" alt="Logo" className="h-12" />
            <div>
              <h1 className="text-xl font-semibold text-[#1A3330] font-['Outfit']">Mind Care</h1>
              <p className="text-xs text-[#4A6B68]">We Treat Without Medicine</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[#E5F2F1] shadow-[0_4px_20px_rgba(13,139,125,0.04)] p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-[#1A3330] font-['Outfit']">Welcome back</h2>
              <p className="text-sm text-[#4A6B68] mt-1">Sign in to your account to continue</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#1A3330] text-sm font-medium">Email or Phone</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A6B68]" />
                  <Input
                    id="email" type="text" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com or +91 9876543210" required
                    className="pl-10 bg-white border-[#E5F2F1] rounded-xl focus:ring-2 focus:ring-[#0D8B7D]/20 focus:border-[#0D8B7D]"
                    data-testid="login-email-input"
                    autoComplete="username"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#1A3330] text-sm font-medium">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A6B68]" />
                  <Input
                    id="password" type={showPw ? 'text' : 'password'}
                    value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="Enter your password" required
                    className="pl-10 pr-10 bg-white border-[#E5F2F1] rounded-xl focus:ring-2 focus:ring-[#0D8B7D]/20 focus:border-[#0D8B7D]"
                    data-testid="login-password-input"
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A6B68]">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {error && <p className="text-sm text-[#D3544E] bg-[#D3544E]/10 p-3 rounded-xl" data-testid="login-error">{error}</p>}

              <Button
                type="submit" disabled={loading}
                className="w-full bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl h-11 font-medium transition-colors"
                data-testid="login-submit-btn"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>

              <button type="button" onClick={() => setShowForgot(true)} className="w-full text-center text-sm text-[#0D8B7D] hover:underline mt-2" data-testid="forgot-password-link">
                Forgot password?
              </button>
            </form>

            {/* Forgot Password Overlay */}
            {showForgot && (
              <div className="mt-4 pt-4 border-t border-[#E5F2F1]">
                {forgotSent ? (
                  <div className="text-center space-y-2" data-testid="forgot-sent">
                    <CheckCircle className="w-8 h-8 text-[#2E8B57] mx-auto" />
                    <p className="text-sm text-[#1A3330] font-medium">Reset link sent!</p>
                    <p className="text-xs text-[#4A6B68]">If an account exists with this email or phone number, a reset link has been sent to the registered email address.</p>
                    <button onClick={() => { setShowForgot(false); setForgotSent(false); }} className="text-xs text-[#0D8B7D] hover:underline">Back to login</button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-3">
                    <p className="text-sm text-[#1A3330] font-medium">Reset your password</p>
                    <Input type="text" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="Email or phone number" required className="rounded-xl border-[#E5F2F1]" data-testid="forgot-email-input" />
                    <div className="flex gap-2">
                      <Button type="submit" disabled={forgotLoading} className="flex-1 bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl text-sm" data-testid="forgot-submit-btn">{forgotLoading ? 'Sending...' : 'Send Reset Link'}</Button>
                      <Button type="button" variant="ghost" onClick={() => setShowForgot(false)} className="text-[#4A6B68] text-sm">Cancel</Button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>

          <p className="text-center text-xs text-[#4A6B68] mt-6">
            Accounts are created by your therapist or administrator.
          </p>
        </div>
      </div>
    </div>
  );
}

